import { UnleashClient } from "unleash-proxy-client";
import { CockpitUser, getCockpitUser, getUser, KeycloakRole, MekUser } from "@newsenec/auth-provider";

export class UnleashAdapter {
    protected companyClient: UnleashClient | undefined;
    protected _userClient: UnleashClient | undefined;

    constructor(private environment: string) {}

    protected get userClient(): UnleashClient {
        if (!this._userClient) throw Error("User client not initialized.");
        return this._userClient;
    }

    isFeatureEnabled = (feature: string): boolean => {
        return (this.userClient.isEnabled(feature) || this.companyClient?.isEnabled(feature)) ?? false;
    };

    /**
     * Starts and initializes the adapter.
     * The adapter can be started multiple times. Only the first start has an effect.
     * @return resolves if the initialization was successful, otherwise rejects
     */
    start = async (): Promise<void> => {
        if (this._userClient) return;

        const user = await getUser();
        this._userClient = await this.createUserClient(this.environment, user);
        this.companyClient = await this.createCompanyClient(this.environment, user);
    };

    private createUserClient = async (environment: string, user?: MekUser): Promise<UnleashClient> => {
        const client = this.createClientForUserId(environment, user?.devNumber);
        const waitForReady = this.waitForClientReady(client);
        await client.start();
        return waitForReady.then(() => client);
    };

    private createCompanyClient = async (environment: string, user?: MekUser): Promise<UnleashClient | undefined> => {
        if (user?.keycloakRoles?.includes(KeycloakRole.Installer) || user?.keycloakRoles?.includes(KeycloakRole.ExternalServiceProvider)) {
            let cockpitUser: CockpitUser;
            try {
                cockpitUser = await getCockpitUser();
            } catch (e) {
                console.debug("Cockpit user profile could not be fetched", e);
                return undefined;
            }
            const client = this.createClientForUserId(environment, cockpitUser.crmUser.companyDevNumber);
            const waitForReady = this.waitForClientReady(client);
            await client.start();
            return waitForReady.then(() => client);
        }

        return undefined;
    };

    private waitForClientReady = (client: UnleashClient): Promise<void> =>
        new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject();
                clearTimeout(timeout);
            }, UnleashAdapter.TIMEOUT_INITIALIZATION_MS);
            const readyCallback = () => {
                resolve();
                clearTimeout(timeout);
            };
            client.on("ready", readyCallback);
            client.on("initialized", readyCallback);
        });

    private createClientForUserId = (environment: string, userId?: string): UnleashClient =>
        new UnleashClient({
            url: location.origin + "/api/fpp/unleash/proxy",
            /*
      The clientKey and appName (used as environment) are effectively set by the proxy.
      However, they seem relevant to associate the metrics.
       */
            clientKey: "s9xX8wkrN8r967z-nGN3",
            appName: environment,
            environment: environment,
            refreshInterval: 600,
            metricsInterval: 1200,
            context: userId ? { userId: userId } : undefined,
        });
    private static TIMEOUT_INITIALIZATION_MS = 1000;
}
